import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { clearFunnelDisplayPasswordSession } from '../utils/funnelDisplayPasswordSession';

// Step types
export type FunnelV3Step =
    | 'motivation' | 'motivation_intro' | 'goals_intro' | 'age' | 'activity' | 'interstitial_metabolism'
    | 'stress_symptoms' | 'sleep' | 'interstitial_sleep' | 'physical_barriers' | 'target_zones' | 'interstitial_science'
    | 'height' | 'current_weight' | 'goal_weight' | 'birth_year'
    | 'target_date' | 'weight_projection'
    | 'walking_history' | 'weight_pattern' | 'interstitial_reframe'
    | 'primary_goal' | 'main_barrier' | 'personalized_blockers' | 'plan_stages' | 'commitment_check' | 'expected_results' | 'testimonial_review' | 'interstitial_rewards' | 'reward_preference'
    | 'review_loading' | 'email_capture' | 'referral_rewards' | 'journey_story' | 'plan_loading' | 'starting_point'
    | 'results' | 'paywall' | 'payment'
    | 'create_account' | 'coaching_upsell' | 'exclusive_offer' | 'get_app';

export type CortisolPatternType = 'full' | 'partial' | 'early';

export type PlanType = '1_week_trial' | '4_week' | '12_week';

export type WeightUnit = 'kg' | 'lbs';
export type HeightUnit = 'cm' | 'ft';

/** Detect imperial locale (US, Liberia, Myanmar use imperial). Retained for potential future locale-based defaults. */
export function detectImperialLocale(): boolean {
    try {
        const locale = Intl.DateTimeFormat().resolvedOptions().locale || navigator.language || '';
        return /^en-US/i.test(locale) || /^en-LR/i.test(locale) || /^my/i.test(locale);
    } catch {
        return false;
    }
}

export interface FunnelV3Data {
    // Quiz answers
    motivation: string | null;
    ageGroup: string | null;
    activityLevel: string | null;
    stressSymptoms: string[];
    sleepQuality: string | null;
    physicalBarriers: string[];
    targetZones: string[];
    heightCm: number | null;
    currentWeightKg: number | null;
    goalWeightKg: number | null;

    // Unit preferences (auto-detected from locale, overridden by user toggle)
    weightUnit: WeightUnit;
    heightUnit: HeightUnit;
    birthYear: number | null;
    targetDate: string | null; // ISO date string or null if no specific date
    healthConsent: boolean;
    walkingHistory: string | null;
    weightPattern: string | null;
    primaryGoal: string | null;
    mainBarrier: string | null;
    rewardPreference: string | null;
    commitment: string | null; // 'absolutely' | 'think_so' | 'not_sure'

    // User info
    firstName: string;
    email: string;
    password: string;

    // Derived/computed
    cortisolPatternType: CortisolPatternType;

    // Payment
    selectedPlan: PlanType | null;
    selectedPlanId: string;
    promoCode: string;
    stripeCouponCode: string;
    validatedCouponId: string;
    promoDiscountPercent: number;
    couponName: string;
    /** Stripe REFERRAL10 when user.referredBy is set and no explicit promo coupon applies */
    referralDiscountPercent: number;
    /** First name from GET /referral/validate — empty when the code is missing or invalid. */
    referrerFirstName: string;
    /** First-charge price in USD cents from the selected Stripe plan (for wallet amount). */
    checkoutBaseAmountCents: number;
    /** Unix ms when selectedPlan was last assigned on paywall (2-day sticky TTL). */
    paywallPlanAssignedAt: number | null;

    // Flow state
    adAngle: string;
    backFromPayment: boolean;
    paymentCompleted: boolean;
    coachingUpsellAccepted: boolean;
    exitDiscountActive: boolean;
}

interface PersonalizedCopy {
    headline: string;
    subheadline: string;
    cortisolLabel: string;
    barrierAcknowledgment: string;
    goalStatement: string;
}

interface FunnelV3State {
    // State
    currentStep: FunnelV3Step;
    data: FunnelV3Data;

    // Actions
    setStep: (step: FunnelV3Step) => void;
    updateField: <K extends keyof FunnelV3Data>(field: K, value: FunnelV3Data[K]) => void;
    resetFunnel: () => void;

    // Computed getters
    getCortisolPattern: () => CortisolPatternType;
    getPersonalizedCopy: () => PersonalizedCopy;
    getTrackingData: () => { em: string; fn: string; external_id: string };
}

// localStorage access can throw (Safari private mode, storage full/disabled).
// Persistence failures must never crash the funnel — worst case the session
// simply doesn't survive a reload.
const guardedLocalStorage = {
    getItem: (name: string): string | null => {
        try {
            return localStorage.getItem(name);
        } catch {
            return null;
        }
    },
    setItem: (name: string, value: string): void => {
        try {
            localStorage.setItem(name, value);
        } catch { /* storage disabled/full — continue in-memory */ }
    },
    removeItem: (name: string): void => {
        try {
            localStorage.removeItem(name);
        } catch { /* storage disabled */ }
    },
};

const getAdAngleFromURL = (): string => {
    if (typeof window === 'undefined') return 'cortisol_loop';
    const params = new URLSearchParams(window.location.search);
    return params.get('angle') || params.get('ad_angle') || 'cortisol_loop';
};

const deriveCortisolPattern = (
    stressSymptoms: string[],
    sleepQuality: string | null,
    weightPattern: string | null
): CortisolPatternType => {
    const stressCount = stressSymptoms.length;
    const poorSleep = sleepQuality === 'wake_during_night' || sleepQuality === 'drained' || sleepQuality === 'struggle_to_sleep';
    const problematicWeight = weightPattern === 'gain_fast_lose_slow' || weightPattern === 'yo_yo';

    if (stressCount >= 3 && poorSleep && problematicWeight) {
        return 'full';
    }
    if (stressCount >= 2 || (poorSleep && problematicWeight)) {
        return 'partial';
    }
    return 'early';
};

const initialData: FunnelV3Data = {
    motivation: null,
    ageGroup: null,
    activityLevel: null,
    stressSymptoms: [],
    sleepQuality: null,
    physicalBarriers: [],
    targetZones: [],
    heightCm: null,
    currentWeightKg: null,
    goalWeightKg: null,
    // Consistent unit defaults per locale (was weightUnit 'kg' + heightUnit 'ft',
    // forcing US users to toggle weight and metric users to toggle height)
    weightUnit: detectImperialLocale() ? 'lbs' : 'kg',
    heightUnit: detectImperialLocale() ? 'ft' : 'cm',
    birthYear: null,
    targetDate: null,
    healthConsent: false,
    walkingHistory: null,
    weightPattern: null,
    primaryGoal: null,
    mainBarrier: null,
    rewardPreference: null,
    commitment: null,
    firstName: '',
    email: '',
    password: '',
    cortisolPatternType: 'early',
    selectedPlan: null,
    selectedPlanId: '',
    promoCode: '',
    stripeCouponCode: '',
    validatedCouponId: '',
    promoDiscountPercent: 0,
    couponName: '',
    referralDiscountPercent: 0,
    referrerFirstName: '',
    checkoutBaseAmountCents: 0,
    paywallPlanAssignedAt: null,
    adAngle: getAdAngleFromURL(),
    backFromPayment: false,
    paymentCompleted: false,
    coachingUpsellAccepted: false,
    exitDiscountActive: false,
};

export const useFunnelV3Store = create<FunnelV3State>()(
    persist(
        (set, get) => ({
            currentStep: 'motivation',
            data: initialData,

            setStep: (step) => set({ currentStep: step }),

            updateField: (field, value) =>
                set((state) => {
                    const newData = { ...state.data, [field]: value };

                    // Recompute cortisol pattern when relevant fields change
                    if (field === 'stressSymptoms' || field === 'sleepQuality' || field === 'weightPattern') {
                        newData.cortisolPatternType = deriveCortisolPattern(
                            newData.stressSymptoms,
                            newData.sleepQuality,
                            newData.weightPattern
                        );
                    }

                    return { data: newData };
                }),

            resetFunnel: () => {
                try {
                    localStorage.removeItem('mc_payment_completed');
                    localStorage.removeItem('mc_predicted_ltv');
                    localStorage.removeItem('mc_card_funding');
                } catch { /* storage disabled */ }
                clearFunnelDisplayPasswordSession();
                set({
                    currentStep: 'motivation',
                    data: { ...initialData, adAngle: getAdAngleFromURL() },
                });
            },

            getCortisolPattern: () => {
                const { stressSymptoms, sleepQuality, weightPattern } = get().data;
                return deriveCortisolPattern(stressSymptoms, sleepQuality, weightPattern);
            },

            getPersonalizedCopy: () => {
                const { cortisolPatternType, primaryGoal, mainBarrier, ageGroup, stressSymptoms } = get().data;

                const cortisolLabels: Record<CortisolPatternType, string> = {
                    full: 'Active Cortisol Loop',
                    partial: 'Partial Cortisol Pattern',
                    early: 'Early-Stage Cortisol Pattern',
                };

                const goalStatements: Record<string, string> = {
                    confidence: 'feel confident in your own skin',
                    health_energy: 'boost your energy and improve your health',
                    clothes_fit: 'fit into your favorite clothes again',
                    sleep_stress: 'sleep better and reduce stress',
                };

                const barrierAcknowledgments: Record<string, string> = {
                    lose_motivation: "It's not about willpower — your body's stress response has been working against you",
                    no_results_fast: "Past programs didn't work because they didn't address the root cause",
                    too_busy: 'Our plan fits into any schedule — just 15-20 minutes a day',
                    uncertainty: "We'll guide you step-by-step with a plan built for your body",
                };

                const headlinesByPattern: Record<CortisolPatternType, string> = {
                    full: 'Your cortisol pattern explains everything',
                    partial: 'We found a cortisol pattern worth addressing',
                    early: 'Good news — you caught this early',
                };

                const hasBellyFat = stressSymptoms.includes('belly_fat');
                const hasNightCravings = stressSymptoms.includes('night_cravings');

                let subheadline = `Women ${ageGroup || '40-49'} with this pattern often struggle with`;
                if (hasBellyFat && hasNightCravings) {
                    subheadline += ' stubborn belly fat and late-night cravings.';
                } else if (hasBellyFat) {
                    subheadline += ' stubborn belly fat that won\'t budge.';
                } else if (hasNightCravings) {
                    subheadline += ' cravings that feel impossible to control.';
                } else {
                    subheadline += ' weight that seems to resist every effort.';
                }

                return {
                    headline: headlinesByPattern[cortisolPatternType],
                    subheadline,
                    cortisolLabel: cortisolLabels[cortisolPatternType],
                    barrierAcknowledgment: barrierAcknowledgments[mainBarrier || 'lose_motivation'],
                    goalStatement: goalStatements[primaryGoal || 'health_energy'],
                };
            },

            getTrackingData: () => {
                const { email, firstName } = get().data;
                const externalId = typeof localStorage !== 'undefined'
                    ? localStorage.getItem('mc_external_id') || ''
                    : '';
                return {
                    em: email,
                    fn: firstName,
                    external_id: externalId,
                };
            },
        }),
        {
            name: 'funnel-v3-storage',
            storage: createJSONStorage(() => guardedLocalStorage),
            version: 15,
            migrate: (persistedState: any, version: number) => {
                // Steps whose progress must survive a reset-style migration.
                // 'paywall' matters most: paywall abandoners are the highest-intent
                // unpaid cohort — wiping them back to 'motivation' loses the sale.
                const postQuizSteps = ['paywall', 'payment', 'create_account', 'coaching_upsell', 'exclusive_offer', 'get_app'];

                if (version < 8) {
                    // v8: Added referral_rewards step between email_capture and paywall.
                    const keep = postQuizSteps.includes(persistedState?.currentStep);
                    if (!keep) {
                        return {
                            ...persistedState,
                            currentStep: 'motivation',
                            data: {
                                ...initialData,
                                adAngle: persistedState?.data?.adAngle || getAdAngleFromURL(),
                            },
                        };
                    }
                }

                if (version < 9) {
                    // v9: Added weightUnit and heightUnit fields
                    const data = persistedState?.data || {};
                    if (!data.weightUnit) data.weightUnit = 'lbs';
                    if (!data.heightUnit) data.heightUnit = 'ft';
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                if (version < 10) {
                    // v10: Added stripeCouponCode and validatedCouponId fields
                    const data = persistedState?.data || {};
                    if (!data.stripeCouponCode) data.stripeCouponCode = '';
                    if (!data.validatedCouponId) data.validatedCouponId = '';
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                if (version < 11) {
                    // v11: Added promoDiscountPercent field
                    const data = persistedState?.data || {};
                    if (!data.promoDiscountPercent) data.promoDiscountPercent = 0;
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                if (version < 12) {
                    // v12: Added couponName field for generic coupon system
                    const data = persistedState?.data || {};
                    if (!data.couponName) data.couponName = '';
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                if (version < 13) {
                    const data = persistedState?.data || {};
                    if (typeof data.referralDiscountPercent !== 'number') data.referralDiscountPercent = 0;
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                if (version < 14) {
                    const data = persistedState?.data || {};
                    if (typeof data.checkoutBaseAmountCents !== 'number') data.checkoutBaseAmountCents = 0;
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                if (version < 15) {
                    const data = persistedState?.data || {};
                    if (data.paywallPlanAssignedAt == null) data.paywallPlanAssignedAt = null;
                    return { ...persistedState, data: { ...initialData, ...data } };
                }

                return persistedState as FunnelV3State;
            },
            partialize: (state) => ({
                ...state,
                data: {
                    ...state.data,
                    password: '',
                },
            }),
            onRehydrateStorage: () => (state) => {
                // Recover paymentCompleted from standalone flag written at charge time.
                // Protects against Zustand hydration loss during the brief window between
                // charge success and store persist commit.
                if (!state) return;
                try {
                    if (localStorage.getItem('mc_payment_completed') === '1' && !state.data.paymentCompleted) {
                        state.data.paymentCompleted = true;
                    }
                } catch { /* storage disabled */ }
            },
        }
    )
);
